import { laborPayStatusLabel, type LaborPayStatus } from "./laborPayList.js";

export type LaborPayInvoiceSort = "serial" | "payout_desc" | "payout_asc" | "newest" | "oldest";

export type LaborPayInvoiceStatusFilter = "all" | LaborPayStatus;

export type LaborPayInvoiceBrowseRow = {
  unit_id: string;
  serial: string;
  recipe_label: string;
  phase_name: string;
  status: LaborPayStatus;
  payout_usd: number;
  admitted_at?: string | null;
  option_labels?: string;
};

export const LABOR_PAY_INVOICE_SORTS: Array<{ id: LaborPayInvoiceSort; label: string }> = [
  { id: "serial", label: "Serial" },
  { id: "payout_desc", label: "Payout high" },
  { id: "payout_asc", label: "Payout low" },
  { id: "newest", label: "Newest" },
  { id: "oldest", label: "Oldest" },
];

export const LABOR_PAY_INVOICE_STATUSES: Array<{ id: LaborPayInvoiceStatusFilter; label: string }> = [
  { id: "all", label: "All statuses" },
  { id: "open", label: "Open" },
  { id: "ops_ok", label: "Ops OK" },
  { id: "plant_ok", label: "Labor OK" },
  { id: "approved", label: "Approved" },
  { id: "payment_requested", label: "Payment requested" },
  { id: "batched", label: "Batched" },
  { id: "void", label: "Void" },
];

/** First non-empty serial. An empty column must not block the spec serial. */
export function displayLaborPaySerial(
  parts: Array<string | null | undefined>,
  fallbackId: string
): string {
  for (const part of parts) {
    const text = (part ?? "").trim();
    if (text) return text;
  }
  return fallbackId.slice(0, 8);
}

function admittedTime(row: LaborPayInvoiceBrowseRow): number {
  const raw = row.admitted_at ?? "";
  const time = raw ? Date.parse(raw) : NaN;
  return Number.isNaN(time) ? 0 : time;
}

export function laborPayInvoiceSearchBlob(row: LaborPayInvoiceBrowseRow): string {
  const time = admittedTime(row);
  const date =
    time > 0
      ? `${new Date(time).toISOString().slice(0, 10)} ${new Date(time).toLocaleDateString()}`
      : "";
  return [
    row.serial,
    row.recipe_label,
    row.phase_name,
    laborPayStatusLabel(row.status),
    row.option_labels ?? "",
    date,
  ]
    .join(" ")
    .toLowerCase();
}

export function filterLaborPayInvoices<T extends LaborPayInvoiceBrowseRow>(
  rows: readonly T[],
  search: string,
  status: LaborPayInvoiceStatusFilter
): T[] {
  const needle = search.trim().toLowerCase();
  return rows.filter((row) => {
    if (status !== "all" && row.status !== status) return false;
    if (!needle) return true;
    return laborPayInvoiceSearchBlob(row).includes(needle);
  });
}

export function sortLaborPayInvoices<T extends LaborPayInvoiceBrowseRow>(
  rows: readonly T[],
  sort: LaborPayInvoiceSort
): T[] {
  const copy = [...rows];
  copy.sort((a, b) => {
    if (sort === "payout_desc" || sort === "payout_asc") {
      const diff = a.payout_usd - b.payout_usd;
      if (diff !== 0) return sort === "payout_desc" ? -diff : diff;
    } else if (sort === "newest" || sort === "oldest") {
      const diff = admittedTime(a) - admittedTime(b);
      if (diff !== 0) return sort === "newest" ? -diff : diff;
    }
    return a.serial.localeCompare(b.serial);
  });
  return copy;
}
